import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  getCoachSeries,
  patchCoachSeries,
  deleteCoachSeries,
  listTables,
  getAdminUser,
  ApiError,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DatePicker } from '../../components/DatePicker';
import { SeriesTimeShiftPanel } from './SeriesTimeShiftPanel';
import { SeriesTableTransferPanel } from './SeriesTableTransferPanel';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { formatClock, todayLocal } from '../../lib/time';
import { fullName } from '../../lib/format';
import {
  durationMinutes,
  occurrenceSummary,
  scheduleLabel,
  seriesStatus,
  type SeriesStatus,
} from '../../lib/coach-series';
import type { AdminLookupUser, CoachSeriesDetail, TableEmbed } from '../../lib/types';
import { orderByDateTimeDescending } from '../../lib/admin-list-order';

export function CoachSeriesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const seriesId = id ? Number(id) : NaN;
  const validId = Number.isFinite(seriesId);

  const seriesLoader = useCallback(
    (signal: AbortSignal) => getCoachSeries(seriesId, signal),
    [seriesId],
  );
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);

  const detail = useApi<CoachSeriesDetail>(seriesLoader);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  // Resolve the coach and the creator by id rather than paging the user list:
  // the old `listUsers({ limit: 200 })` join silently degraded to a raw UUID
  // once a club grew past 200 users. Both ids only exist after the series
  // loads, so the loaders no-op until then. Removed entirely once
  // givip/prospin-api#106 embeds the coach in the series payload.
  const coachUserId = detail.data?.series.coach_user_id ?? null;
  const createdById = detail.data?.series.created_by ?? null;

  const coachLoader = useCallback(
    (signal: AbortSignal) =>
      coachUserId ? getAdminUser(coachUserId, signal) : Promise.resolve(null),
    [coachUserId],
  );
  const creatorLoader = useCallback(
    (signal: AbortSignal) =>
      createdById ? getAdminUser(createdById, signal) : Promise.resolve(null),
    [createdById],
  );

  const coach = useApi<AdminLookupUser | null>(coachLoader);
  const creator = useApi<AdminLookupUser | null>(creatorLoader);
  const upcomingOccurrences = useMemo(
    () => orderByDateTimeDescending(
      detail.data?.upcoming_occurrences ?? [],
      (occurrence) => ({ date: occurrence.date, time: occurrence.start_time, id: occurrence.id }),
    ),
    [detail.data?.upcoming_occurrences],
  );

  const [newEndsOn, setNewEndsOn] = useState('');
  const [savingEnd, setSavingEnd] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!validId) {
    return (
      <div style={{ ...T.ui, color: PS.danger }}>
        Invalid series id.
      </div>
    );
  }

  const series = detail.data?.series ?? null;
  const status = series ? seriesStatus(series.ends_on, todayLocal()) : null;
  const duration = series ? durationMinutes(series.start_time, series.end_time) : null;
  // A one-time session is a series row, but calling it one everywhere makes
  // staff cross-reference `starts_on`/`ends_on` to work out what they're
  // looking at. The API owns the flag (givip/prospin-api#83); this page only
  // words itself around it.
  const isOneTime = series?.is_one_time === true;
  const noun = isOneTime ? 'One-time coach session' : 'Coach series';

  const handlePatchEndsOn = async (nextEnd: string | null) => {
    setSavingEnd(true);
    try {
      await patchCoachSeries(seriesId, { ends_on: nextEnd });
      push('success', nextEnd ? `End set to ${nextEnd}.` : 'End cleared (open-ended).');
      setNewEndsOn('');
      detail.refetch();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Update failed');
    } finally {
      setSavingEnd(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCoachSeries(seriesId);
      push(
        'success',
        isOneTime
          ? `Session #${seriesId} cancelled.`
          : `Series #${seriesId} ended. Future occurrences removed.`,
      );
      setDeleteOpen(false);
      navigate('/coaches');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Delete failed');
    }
  };

  const tableName = (tableId: number) =>
    (tables.data ?? []).find((t) => t.id === tableId)?.name ?? `#${tableId}`;

  /** Name + phone for a resolved user; the raw id while loading or on failure. */
  const userLabel = (user: AdminLookupUser | null, fallbackId: string) => {
    if (!user) return `#${fallbackId}`;
    return user.phone ? `${fullName(user)} · ${user.phone}` : fullName(user);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>
          {noun} {series ? `#${series.id}` : ''}
        </h1>
        {status && <StatusBadge status={status} />}
        <Button variant="secondary" onClick={() => navigate('/coaches')}>
          ← Back
        </Button>
      </header>

      {detail.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}

      {detail.error && (
        <div
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            color: PS.errorText,
            borderRadius: 3,
          }}
        >
          {detail.error.message}
        </div>
      )}

      {detail.data && series && (
        <>
          <section
            style={{
              background: PS.white,
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 4,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <h2 style={{ ...T.subheading, margin: 0 }}>{isOneTime ? 'Session' : 'Series'}</h2>

            {/* Status is the badge beside the page title — deliberately not
                repeated as a row here. */}
            <Row
              label="Coach"
              value={
                <Link
                  to={`/users/${series.coach_user_id}`}
                  style={{ color: PS.linkBlue, textDecoration: 'none' }}
                >
                  {userLabel(coach.data, series.coach_user_id)}
                </Link>
              }
            />
            {/* `testId`: the table name also renders inside the transfer
                picker's <option> list, so a text query for it can pass while
                this row still shows the old table. The e2e transfer spec
                asserts on the row itself. */}
            <Row label="Table" value={tableName(series.table_id)} testId="series-table" />
            <Row label={isOneTime ? 'When' : 'Recurrence'} value={scheduleLabel(series)} />
            <Row
              label="Time"
              value={
                `${formatClock(series.start_time)}–${formatClock(series.end_time)}` +
                (duration !== null ? ` · ${duration} min` : '')
              }
            />
            {/* `starts_on == ends_on` is *what makes* this a one-time session:
                showing both reads as a coincidence, and the When row above
                already carries the single date. */}
            {!isOneTime && (
              <>
                <Row label="Starts on" value={series.starts_on} />
                <Row label="Ends on" value={series.ends_on ?? '— (open-ended)'} />
              </>
            )}
            <Row label="Upcoming" value={occurrenceSummary(detail.data.upcoming_occurrences)} />
            <Row label={isOneTime ? 'Session id' : 'Series id'} value={`#${series.id}`} />
            <Row label="Created by" value={userLabel(creator.data, series.created_by)} />

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                borderTop: `1px dashed ${PS.borderLight}`,
                paddingTop: 12,
              }}
            >
              {/* Table transfer (api#85). Offered on both kinds, unlike the
                  shift below: a session booked onto the wrong table is exactly
                  what this endpoint fixes, and `/table` has no one-time
                  restriction. See
                  .ai/features/49-series-table-transfer/DECISIONS.md. */}
              {/* Gated on the list having arrived, not `?? []`: with an empty
                  list the panel truthfully reports there is nowhere to
                  transfer to, and asserting that about a club whose tables we
                  simply have not fetched yet is a lie with a plausible face. */}
              {tables.data && (
                <SeriesTableTransferPanel
                  seriesId={seriesId}
                  currentTableId={series.table_id}
                  tables={tables.data}
                  onTransferred={detail.refetch}
                />
              )}

              {isOneTime ? (
                // The API rejects PATCH on a one-time (400): an omitted
                // `ends_on` is indistinguishable from an explicit null, which
                // would silently turn this session into an open-ended weekly
                // series. Not offering the control beats surfacing the error
                // after the fact.
                <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
                  A one-time session cannot be re-scoped into a recurring series, and its time and
                  coach cannot be edited — cancel it and book another if you need to change those.
                </p>
              ) : (
                <>
                  {/* Same-day window shift (api#84). Offered on recurring
                      series only: the endpoint would take a one-time too, but
                      the copy above tells staff a one-time's time is fixed, and
                      previewing "which of the 1 occurrences move" is noise. */}
                  <SeriesTimeShiftPanel
                    seriesId={seriesId}
                    startTime={series.start_time}
                    endTime={series.end_time}
                    onShifted={detail.refetch}
                  />

                  <span style={{ ...T.label, marginTop: 8 }}>Change end date</span>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <DatePicker value={newEndsOn} onChange={setNewEndsOn} />
                    <Button
                      variant="secondary"
                      onClick={() => void handlePatchEndsOn(newEndsOn || null)}
                      disabled={savingEnd}
                    >
                      {savingEnd ? 'Saving…' : 'Update'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => void handlePatchEndsOn(null)}
                      disabled={savingEnd || series.ends_on === null}
                    >
                      Clear (make open-ended)
                    </Button>
                  </div>
                  <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
                    Other fields (weekday, coach) cannot be edited — delete and recreate if you
                    need to change those.
                  </p>
                </>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                {isOneTime ? 'Cancel this session' : 'End series + remove future occurrences'}
              </Button>
            </div>
          </section>

          <section>
            <h2 style={{ ...T.subheading, margin: '0 0 8px' }}>
              Upcoming occurrences ({upcomingOccurrences.length})
            </h2>
            {upcomingOccurrences.length === 0 ? (
              <div style={{ ...T.caption, color: PS.textSec }}>
                No upcoming occurrences in the next 31 days.
              </div>
            ) : (
              <div
                style={{
                  background: PS.white,
                  border: `1px solid ${PS.borderLight}`,
                  borderRadius: 4,
                  overflowX: 'auto',
                  // overflow-x:auto implicitly makes overflow-y `auto`, which
                  // traps vertical page scroll on touch. Pin it to hidden.
                  overflowY: 'hidden',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                      <th style={th}>Date</th>
                      <th style={th}>Time</th>
                      <th style={th}>Status</th>
                      <th style={th}>Booking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingOccurrences.map((o) => (
                      <tr key={o.id} style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                        <td style={td}>{o.date}</td>
                        <td style={td}>
                          {formatClock(o.start_time)}–{formatClock(o.end_time)}
                          {o.was_shortened && (
                            <span
                              style={{
                                marginLeft: 6,
                                ...T.microLabel,
                                color: PS.warning,
                                textTransform: 'none',
                              }}
                            >
                              (shortened)
                            </span>
                          )}
                        </td>
                        <td style={td}>{o.status}</td>
                        <td style={td}>
                          <code>#{o.id}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title={isOneTime ? 'Cancel this session?' : 'End coach series?'}
        message={
          isOneTime
            ? 'Cancel this one-time coach session and free the slot. If the session is already in the past it stays on record. This cannot be undone.'
            : 'Set ends_on to yesterday and cancel all future materialized occurrences for this series. Past occurrences remain. This cannot be undone.'
        }
        confirmLabel={isOneTime ? 'Cancel session' : 'End series'}
        destructive
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  );
}

const th: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontFamily: "'Barlow Condensed',sans-serif",
  fontSize: 12,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: PS.textSec,
};

const td: CSSProperties = {
  padding: '10px 12px',
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  color: PS.nearBlack,
};

function Row({
  label,
  value,
  testId,
}: {
  label: string;
  value: ReactNode;
  testId?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <div style={{ ...T.label, width: 120, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack }} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: SeriesStatus }) {
  const active = status === 'active';
  return (
    <span
      style={{
        ...T.microLabel,
        color: active ? PS.success : PS.textSec,
        background: active ? PS.successSoft : PS.surfaceDisabled,
        border: `1px solid ${active ? PS.success : PS.border}`,
        borderRadius: 3,
        padding: '2px 8px',
      }}
    >
      {active ? 'Active' : 'Ended'}
    </span>
  );
}
